/**
 * Settings → Agents ("Agent mode"): connect the person's own AI agent to their
 * account (spec 2026-10-02-agent-connector).
 *
 * The thing to copy is one sentence with no secret in it. The agent reads the
 * guide, sends the person an approve link, and collects its own key after one
 * tap here — the key never passes through a chat. The connector link for chat
 * apps that cannot make requests themselves is the quiet footnote.
 *
 * @module web/screens/app/AgentSection
 */
import { useEffect, useRef, useState } from "react";
import { ApiError, del, get, post } from "../../api/client.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { approveCodeFromHash, lastUsedText, setupPrompt, type AgentKeyInfo, type MintedKey } from "./agentPanel.ts";

function errorText(e: unknown): string {
  if (!(e instanceof ApiError)) return "The connection was interrupted. Try again.";
  const code = (e.body as { error?: string } | null)?.error;
  if (code === "needs_personal_account") return "Sign in with your own account first, then connect an agent.";
  if (code === "expired") return "This link expired. Ask your agent to start the setup again.";
  if (code === "already_used") return "This link was already used. Ask your agent to start the setup again.";
  if (code === "not_found") return "This link isn't valid. Ask your agent to start the setup again.";
  return e.message;
}

const CopyIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="3" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>;
const CheckIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
const CopyHint = ({ done, doneText }: { done: boolean; doneText: string }) => done
  ? <><CheckIcon />{doneText}</>
  : <><CopyIcon /><span className="agent-hint-touch">Tap to copy</span><span className="agent-hint-fine">Click to copy</span></>;

export function AgentSection() {
  const { brand } = useBrand();
  const personal = !!brand?.personal;
  const [key, setKey] = useState<AgentKeyInfo | null>(null);
  const [connector, setConnector] = useState<MintedKey | null>(null);
  const [approveCode, setApproveCode] = useState(() => approveCodeFromHash(window.location.hash));
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState<"prompt" | "connector" | null>(null);
  const promptText = useRef<HTMLSpanElement>(null);
  const connectorText = useRef<HTMLSpanElement>(null);
  const prompt = setupPrompt(`${window.location.origin}/agent.md`);

  useEffect(() => {
    const onHash = () => { setApproveCode(approveCodeFromHash(window.location.hash)); setApproved(false); setError(""); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!personal) return;
    let live = true;
    void get<{ key: AgentKeyInfo | null }>("/api/agent-keys")
      .then(r => { if (live) setKey(r.key); })
      .catch(e => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [personal, approved]);

  async function approve(): Promise<void> {
    if (busy || !approveCode) return;
    setBusy(true); setError("");
    try { await post("/api/agent-link/approve", { code: approveCode }); setApproved(true); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  }

  function leaveApprove(): void {
    window.location.hash = "settings/agents";
  }

  async function createConnector(): Promise<void> {
    if (busy) return;
    if (key && !window.confirm("Create a connector link? Your current agent stops working right away.")) return;
    setBusy(true); setError(""); setCopied(null);
    try { const r = await post<MintedKey>("/api/agent-keys"); setConnector(r); setKey(r.key); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  }

  async function disconnect(): Promise<void> {
    if (busy || !window.confirm("Disconnect your agent? It loses access to your account right away.")) return;
    setBusy(true); setError("");
    try { await del("/api/agent-keys"); setKey(null); setConnector(null); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  }

  async function copy(what: "prompt" | "connector", value: string, fallback: HTMLElement | null): Promise<void> {
    try { await navigator.clipboard.writeText(value); setCopied(what); }
    catch {
      // No clipboard (old browser, denied permission): select the text so a long-press or ⌘C still works.
      if (!fallback) return;
      const range = document.createRange(); range.selectNodeContents(fallback);
      const sel = window.getSelection(); sel?.removeAllRanges(); sel?.addRange(range);
    }
  }

  // ---- the one tap: an agent asked to connect ----
  if (approveCode) {
    return <section className="agent-screen" aria-label="Approve agent">
      {approved ? <div className="agent-approve">
        <span className="agent-approve-mark agent-approve-mark-done" aria-hidden="true"><CheckIcon /></span>
        <h1 className="t-h2">Your agent is connected</h1>
        <p className="t-body agent-muted">Go back to your agent. It picks up the connection by itself in a few seconds.</p>
        <button className="btn btn-ghost agent-done" type="button" onClick={leaveApprove}>Done</button>
      </div> : <div className="agent-approve">
        <span className="agent-approve-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="7" width="16" height="12" rx="4" /><path d="M12 7V4" /><circle cx="9.5" cy="13" r="1" fill="currentColor" stroke="none" /><circle cx="14.5" cy="13" r="1" fill="currentColor" stroke="none" /><path d="M2 12v2M22 12v2" /></svg>
        </span>
        <h1 className="t-h2">Connect your agent?</h1>
        <p className="t-body agent-muted">It can brief, check and comment as {brand?.self?.name || "you"}, with your role on every brand you're on. It always asks for your “go” before ordering.</p>
        {error && <p className="team-error" role="alert">{error}</p>}
        {!personal ? <p className="t-body-sm agent-muted">Sign in as yourself (Google, Apple or password) to approve. This page was opened with a shared brand link.</p> : <>
          <button className="btn btn-brand agent-cta" type="button" disabled={busy} onClick={() => void approve()}>{busy ? "Connecting…" : "Approve"}</button>
          {key && <p className="agent-note">This replaces your current agent connection.</p>}
          <p className="agent-note">Code {approveCode}. Only approve if you just asked your agent to connect.</p>
        </>}
        <button className="btn btn-ghost agent-done" type="button" onClick={leaveApprove}>Not now</button>
      </div>}
    </section>;
  }

  return <section className="agent-screen" aria-label="Agents">
    <header className="page-head">
      <h1 className="t-h2">Agent mode</h1>
      <p className="t-body">Brief, check and comment on your ads from your own AI agent: Claude, ChatGPT, Codex or any other.</p>
    </header>
    {error && <p className="team-error" role="alert">{error}</p>}

    <div className="agent-fresh">
      <p className="agent-eyebrow">Give this to your agent</p>
      <button type="button" className="agent-copy" data-copied={copied === "prompt" || undefined}
        aria-label="Copy the message for your agent" onClick={() => void copy("prompt", prompt, promptText.current)}>
        <span className="agent-copy-text" ref={promptText}>{prompt}</span>
        <span className="agent-copy-hint" aria-live="polite"><CopyHint done={copied === "prompt"} doneText="Copied. Now paste it into your agent" /></span>
      </button>
      <p className="agent-note">Your agent sends you a link. Tap Approve, and it's connected. No key ever goes into your chat.</p>
    </div>

    {personal && key && !connector && <div className="agent-card">
      <div className="team-row">
        <div className="team-person"><strong>Agent connected</strong><span>{lastUsedText(key.lastUsedAt)}</span></div>
        <button className="btn btn-ghost agent-danger" type="button" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>
      </div>
    </div>}

    {connector ? <div className="agent-card">
      <p className="agent-eyebrow">Your connector link</p>
      <button type="button" className="agent-copy" data-copied={copied === "connector" || undefined}
        aria-label="Copy the connector link" onClick={() => void copy("connector", connector.connectorUrl, connectorText.current)}>
        <span className="agent-copy-text agent-copy-mono" ref={connectorText}>{connector.connectorUrl}</span>
        <span className="agent-copy-hint" aria-live="polite"><CopyHint done={copied === "connector"} doneText="Copied" /></span>
      </button>
      <p className="agent-note">In Claude: <a href="https://claude.ai/settings/connectors" target="_blank" rel="noreferrer">Settings → Connectors</a> → Add custom connector, then paste it. Shown once. It works like your login, so keep it private.</p>
      <button className="btn btn-ghost agent-done" type="button" onClick={() => setConnector(null)}>Done</button>
    </div> : personal && <p className="agent-aside">
      Using Claude.ai or ChatGPT in the browser? <button type="button" className="agent-inline" disabled={busy} onClick={() => void createConnector()}>Create a connector link</button> instead.
    </p>}

    {!personal && <p className="agent-aside">To approve your agent, sign in as yourself (Google, Apple or password). This page was opened with a shared brand link.</p>}
  </section>;
}
